import {useState, useCallback, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {TState} from '../redux/store.type';
import {getEventSettings} from '../redux/event/eventActions';
import {UseSendDataConfig, SendData} from './sendHooks.types';
import {useDefaultFbImage} from './useDefaultFbImage';
import {serializeTemplateStructure} from '../component/whatsAppTemplateEditor/utils/formatUtils';
import {TemplateStructure} from '../component/whatsAppTemplateEditor/types/templateStructure';
import {getWhatsAppImagePath} from '../pages/branding/whatsAppScreen/getWhatsAppImagePath';
import {isImagePathSupported} from '../pages/branding/whatsAppScreen/whatsAppValidation';

type WhatsappEdits = Partial<{
    image: {path: string, imageId: string};
    message: string | null;
    whatsappTemplate: TemplateStructure | null;
}>;

type EmailEdits = Partial<{
    image: {path: string, imageId: string};
}>;

export const useSendData = ({defaultSubject, defaultEmailBody, defaultSmsMessage, type, hasWhatsapp = true}: UseSendDataConfig) => {
    const savedData = useSelector((s: TState) => type ? s.mailing.designMailing?.[type] : null);
    const getEventSettingsXHR = useSelector((s: TState) => s.event.getEventSettingsXHR);
    const brandingWhatsappTemplate = hasWhatsapp && type ? useSelector((s: TState) => s.branding[`${type}WhatsappTemplate`]) : null;
    const whatsappTemplate = hasWhatsapp ? brandingWhatsappTemplate : null;
    const defaultWhatsappImage = useDefaultFbImage(true);
    const defaultEmailImage = useDefaultFbImage(false);
    const eventType = useSelector((s: TState) => s.event.type);

    const [whatsappEdits, setWhatsappEdits] = useState<WhatsappEdits>({});
    const [emailEdits, setEmailEdits] = useState<EmailEdits>({});

    const resolvedWhatsappImage = useMemo(() => {
        const image = whatsappEdits.image ?? savedData?.whatsappImage ?? defaultWhatsappImage;
        if(image?.path && isImagePathSupported(image.path)) {
            return image;
        }
        return {path: getWhatsAppImagePath(image, defaultWhatsappImage, eventType ?? ''), imageId: image?.imageId ?? ''};
    }, [whatsappEdits.image, savedData?.whatsappImage, defaultWhatsappImage, eventType]);

    const whatsappData = useMemo(() => {
        let message: string | null = null;
        let template: TemplateStructure | null = null;

        if(hasWhatsapp && savedData?.whatsappContent) {
            const parsed = JSON.parse(savedData.whatsappContent);
            if(Array.isArray(parsed)) {
                template = parsed as TemplateStructure;
                message = serializeTemplateStructure(template);
            }
        } else if(hasWhatsapp && whatsappTemplate) {
            template = whatsappTemplate as unknown as TemplateStructure;
            message = serializeTemplateStructure(template);
        }

        return {
            image: resolvedWhatsappImage,
            message: whatsappEdits.message ?? message,
            whatsappTemplate: whatsappEdits.whatsappTemplate ?? template
        };
    }, [whatsappEdits, resolvedWhatsappImage, savedData?.whatsappContent, whatsappTemplate, hasWhatsapp]);

    const resolvedEmailImage = useMemo(() => {
        return emailEdits.image ?? savedData?.emailImage ?? defaultEmailImage;
    }, [emailEdits.image, savedData?.emailImage, defaultEmailImage]);

    const [emailContent, setEmailContent] = useState({
        emailBody: savedData?.emailContent || defaultEmailBody,
        emailSubject: savedData?.emailSubject || defaultSubject,
    });

    const emailData = useMemo(() => ({
        image: resolvedEmailImage,
        emailBody: emailContent.emailBody,
        emailSubject: emailContent.emailSubject,
    }), [resolvedEmailImage, emailContent]);

    const [smsData, setSmsData] = useState({message: savedData?.smsContent || defaultSmsMessage});

    const handleDataSave = useCallback((data: unknown, key: string, channel: keyof SendData) => {
        if(channel === 'whatsapp') {
            setWhatsappEdits(prev => ({...prev, [key]: data}));
            return;
        }
        if(channel === 'email') {
            if(key === 'image') {
                setEmailEdits(prev => ({...prev, image: data as EmailEdits['image']}));
                return;
            }
            setEmailContent(prev => ({...prev, [key]: data}));
            return;
        }
        if(channel === 'sms') {
            setSmsData(prev => ({...prev, [key]: data}));
        }
    }, []);

    useEffect(() => {
        if(!getEventSettingsXHR) {
            getEventSettings().then();
        }
    }, [getEventSettingsXHR]);

    useEffect(() => {
        if(!savedData?.whatsappContent) return;
        setWhatsappEdits(prev => {
            const {message, whatsappTemplate, image, ...rest} = prev;
            return rest;
        });
    }, [savedData?.whatsappContent]);

    useEffect(() => {
        if(!savedData) return;
        if(savedData.whatsappContent) {
            setWhatsappEdits(prev => {
                const {message, whatsappTemplate, image, ...rest} = prev;
                return rest;
            });
        }
        if(savedData.emailContent) {
            setEmailEdits(prev => {
                const {image, ...rest} = prev;
                return rest;
            });
            setEmailContent(prev => ({
                ...prev,
                emailBody: savedData.emailContent!,
                ...(savedData.emailSubject ? {emailSubject: savedData.emailSubject} : {}),
            }));
        }
        if(savedData.smsContent) {
            setSmsData({message: savedData.smsContent});
        }
    }, [savedData]);

    return {whatsappData, emailData, smsData, handleDataSave, whatsappTemplate};
};
